import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { Language, LanguageState } from '@/types';

interface LanguageProviderProps {
  children: React.ReactNode;
  defaultLanguage?: Language;
  storageKey?: string;
}

// Translation dictionary
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    'nav.records': 'Records',
    'nav.analytics': 'Analytics',
    'nav.admin': 'Admin',
    'nav.appointments': 'Appointments',
    'nav.inventory': 'Inventory',
    'nav.temperature': 'Temperature',
    'nav.dashboard': 'Dashboard',

    // Common
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.export': 'Export',
    'common.refresh': 'Refresh',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.submit': 'Submit',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.success': 'Success',
    'common.error': 'Error',
    'common.warning': 'Warning',
    'common.info': 'Info',
    'common.actions': 'Actions',
    'common.actions.tooltip': 'Actions',

    // Status
    'status.all': 'All',
    'status.resolved': 'Resolved',
    'status.pending': 'Pending',
    'status.resolvedMessage': 'Call marked as resolved!',
    'status.pendingMessage': 'Call marked as pending!',
    'status.connected': 'Connected',
    'status.disconnected': 'Disconnected',
    'status.connecting': 'Connecting...',

    // Table
    'table.records': 'records',
    'table.showing': 'Showing',
    'table.of': 'of',
    'table.previous': 'Previous',
    'table.next': 'Next',
    'table.noRecords': 'No records found',
    'table.actions': 'Actions',
    'table.tableName': 'Table:',

    // Pagination
    'pagination.showing': 'Showing',
    'pagination.of': 'of',

    // Filters
    'filter.quick': 'Quick:',
    'filter.custom': 'Custom:',
    'filter.all': 'All',
    'filter.today': 'Today',
    'filter.yesterday': 'Yesterday',
    'filter.thisWeek': 'This Week',
    'filter.lastWeek': 'Last Week',
    'filter.thisMonth': 'This Month',
    'filter.lastMonth': 'Last Month',
    'filter.apply': 'Apply',
    'filter.clear': 'Clear',
    'filter.selectCategory': 'Select Category',
    'filter.selectServiceType': 'Select Service Type',
    'filter.inquiry': 'Inquiry',

    // Metrics
    'metrics.totalCalls': 'Total Calls',
    'metrics.resolved': 'Resolved',
    'metrics.pending': 'Pending',
    'metrics.avgDuration': 'Avg Duration',
    'metrics.resolutionRate': 'Resolution Rate',
    'metrics.highUrgencyRate': 'High Urgency Rate',
    'metrics.atRiskCalls': 'At-Risk Calls',
    'metrics.peakHour': 'Peak Hour',
    'metrics.resolutionRateCard': 'Resolution Rate',
    'metrics.avgCallLength': 'average call length',
    'metrics.highExtraHighUrgency': 'high + extra high urgency',
    'metrics.highUrgencyPending': 'high urgency, pending',
    'metrics.calls': 'calls',
    'metrics.ofAllCalls': 'of all calls',

    // Charts
    'charts.callVolume': 'Call Volume Over Time',
    'charts.callVolumeDesc': 'Daily call volume trends',
    'charts.statusDist': 'Status Distribution',
    'charts.statusDistDesc': 'Resolved vs Pending calls',
    'charts.callsByDay': 'Calls by Day of Week',
    'charts.callsByDayDesc': 'Call distribution by day of week',
    'charts.durationDist': 'Call Duration Distribution',
    'charts.durationDistDesc': 'Call duration breakdown',
    'charts.peakHours': 'Peak Call Hours',
    'charts.peakHoursDesc': 'Busiest hours of the day (top 8)',
    'charts.resolutionTrend': 'Resolution Rate Trend',
    'charts.avgDurationByDay': 'Avg Duration by Day',
    'charts.cumulativeCalls': 'Cumulative Calls',
    'charts.statusByDay': 'Status Breakdown by Day',
    'charts.callsByCategory': 'Calls by Category',
    'charts.callsByCategoryDesc': 'Top categories by call volume',
    'charts.callsByService': 'Calls by Service Type',
    'charts.callsByServiceDesc': 'Service type distribution',
    'charts.urgencyDist': 'Urgency Distribution',
    'charts.urgencyDistDesc': 'Calls by urgency level',
    'charts.urgencyAnalysis': 'Urgency Analysis',
    'charts.urgencyAnalysisDesc': 'Resolved and pending calls by urgency level',
    'charts.avgHandleTime': 'Avg Handle Time by Category',
    'charts.avgHandleTimeDesc': 'Average call duration by category',
    'charts.serviceEfficiency': 'Service Type Efficiency',
    'charts.serviceEfficiencyDesc': 'Resolution rate by service type (percentage)',

    // Days
    'days.sun': 'Sun',
    'days.mon': 'Mon',
    'days.tue': 'Tue',
    'days.wed': 'Wed',
    'days.thu': 'Thu',
    'days.fri': 'Fri',
    'days.sat': 'Sat',

    // Duration
    'duration.0-30s': '0-30s',
    'duration.30-60s': '30-60s',
    'duration.1-2m': '1-2m',
    'duration.2-5m': '2-5m',
    'duration.5m+': '5m+',

    // Modals
    'modal.recordDetails': 'Record Details',
    'modal.deleteTitle': 'Delete Record',
    'modal.deleteMessage': 'Are you sure you want to delete this record? This action cannot be undone.',
    'modal.bulkDeleteTitle': 'Delete Records',
    'modal.bulkDeleteMessage': 'Are you sure you want to delete {count} records? This action cannot be undone.',

    // Actions
    'actions.markResolved': 'Mark as Resolved',
    'actions.markPending': 'Mark as Pending',
    'actions.playAudio': 'Play Audio',
    'actions.stopAudio': 'Stop Audio',
    'actions.exportCSV': 'Export CSV',
    'actions.exportPDF': 'Export PDF',

    // Toast
    'toast.recordUpdated': 'Record updated successfully',
    'toast.recordDeleted': 'Record deleted successfully',
    'toast.recordsDeleted': '{count} records deleted successfully',
    'toast.exportSuccess': 'Export completed successfully',
    'toast.errorOccurred': 'An error occurred',
    'toast.connectionLost': 'Connection lost. Attempting to reconnect...',
    'toast.connectionRestored': 'Connection restored',

    // Empty states
    'empty.noRecords': 'No records found',
    'empty.noRecordsDesc': 'The table is empty or no data matches your search.',
    'empty.noCategory': 'No category data',
    'empty.noServiceType': 'No service type data',

    // Accessibility
    'a11y.toggleTheme': 'Toggle theme',
    'a11y.toggleLanguage': 'Toggle language',
    'a11y.toggleSidebar': 'Toggle sidebar',
    'a11y.sortColumn': 'Sort by {column}',
    'a11y.filterRecords': 'Filter records',
    'a11y.exportData': 'Export data',

    // Placeholders
    'placeholder.search': 'Search records...',
    'placeholder.phoneNumber': 'Phone number',
    'placeholder.name': 'Name',
    'placeholder.notes': 'Notes...',

    // Appointments
    'appointments.addNew': 'Add Appointment',
    'appointments.edit': 'Edit Appointment',
    'appointments.searchPlaceholder': 'Search appointments...',
    'appointments.patientName': 'Patient Name',
    'appointments.patientPlaceholder': 'Enter patient name',
    'appointments.date': 'Date',
    'appointments.time': 'Time',
    'appointments.provider': 'Provider',
    'appointments.providerPlaceholder': 'Enter provider name',
    'appointments.status': 'Status',
    'appointments.notes': 'Notes',
    'appointments.notesPlaceholder': 'Enter notes',
    'appointments.allStatus': 'All Status',
    'appointments.scheduled': 'Scheduled',
    'appointments.confirmed': 'Confirmed',
    'appointments.completed': 'Completed',
    'appointments.cancelled': 'Cancelled',
    'appointments.noShow': 'No Show',
    'appointments.noAppointments': 'No appointments found',
    'appointments.confirmDelete': 'Are you sure you want to delete this appointment?',

    // Inventory
    'inventory.addNew': 'Add Item',
    'inventory.edit': 'Edit Item',
    'inventory.searchPlaceholder': 'Search inventory...',
    'inventory.itemName': 'Item Name',
    'inventory.itemPlaceholder': 'Enter item name',
    'inventory.sku': 'SKU',
    'inventory.quantity': 'Qty',
    'inventory.unit': 'Unit',
    'inventory.price': 'Price',
    'inventory.category': 'Category',
    'inventory.categoryPlaceholder': 'Enter category',
    'inventory.expiryDate': 'Expiry Date',
    'inventory.status': 'Status',
    'inventory.allCategories': 'All Categories',
    'inventory.totalItems': 'Total Items',
    'inventory.totalValue': 'Total Value',
    'inventory.lowStock': 'Low Stock',
    'inventory.inStock': 'In Stock',
    'inventory.outOfStock': 'Out of Stock',
    'inventory.noItems': 'No inventory items found',
    'inventory.confirmDelete': 'Are you sure you want to delete this item?',

    // User Menu
    'user.profile': 'Profile',
    'user.settings': 'Settings',
    'user.logout': 'Logout',
    'user.welcome': 'Welcome',

    // Field Labels
    'field.id': 'ID',
    'field.date': 'Date',
    'field.name': 'Name',
    'field.phone': 'Phone',
    'field.category': 'Category',
    'field.service': 'Service',
    'field.inquiry': 'Inquiry',
    'field.duration': 'Duration',
    'field.status': 'Status',
    'field.urgency': 'Urgency',
    'field.notes': 'Notes',
    'field.summary': 'Summary',
    'field.conversationId': 'Conversation ID',
    'field.audio': 'Audio',

    // Urgency Levels
    'urgency.low': 'Low',
    'urgency.medium': 'Medium',
    'urgency.high': 'High',
    'urgency.extraHigh': 'Extra High',
    'urgency.no': 'No',

    // Quick Filters
    'quickFilter.quick': 'Quick:',

    // Date Range
    'dateRange.from': 'From',
    'dateRange.until': 'Until',
    'dateRange.last7Days': 'Last 7 days',
    'dateRange.last30Days': 'Last 30 days',
    'dateRange.last90Days': 'Last 90 days',
  },
  ar: {
    // Navigation
    'nav.records': 'السجلات',
    'nav.analytics': 'التحليلات',
    'nav.admin': 'الإدارة',
    'nav.appointments': 'المواعيد',
    'nav.inventory': 'المخزون',
    'nav.temperature': 'درجات الحرارة',
    'nav.dashboard': 'لوحة التحكم',

    // Common
    'common.loading': 'جاري التحميل...',
    'common.save': 'حفظ',
    'common.cancel': 'إلغاء',
    'common.delete': 'حذف',
    'common.edit': 'تعديل',
    'common.view': 'عرض',
    'common.search': 'بحث',
    'common.filter': 'تصفية',
    'common.export': 'تصدير',
    'common.refresh': 'تحديث',
    'common.download': 'تحميل',
    'common.upload': 'رفع',
    'common.submit': 'إرسال',
    'common.close': 'إغلاق',
    'common.confirm': 'تأكيد',
    'common.success': 'نجاح',
    'common.error': 'خطأ',
    'common.warning': 'تحذير',
    'common.info': 'معلومات',
    'common.actions': 'الإجراءات',
    'common.actions.tooltip': 'الإجراءات',

    // Status
    'status.all': 'الكل',
    'status.resolved': 'تم الحل',
    'status.pending': 'قيد الانتظار',
    'status.resolvedMessage': 'تم وضع علامة المكالمة كمحلولة!',
    'status.pendingMessage': 'تم وضع علامة المكالمة قيد الانتظار!',
    'status.connected': 'متصل',
    'status.disconnected': 'غير متصل',
    'status.connecting': 'جاري الاتصال...',

    // Table
    'table.records': 'سجلات',
    'table.showing': 'عرض',
    'table.of': 'من',
    'table.previous': 'السابق',
    'table.next': 'التالي',
    'table.noRecords': 'لا توجد سجلات',
    'table.actions': 'الإجراءات',
    'table.tableName': 'الجدول:',

    // Pagination
    'pagination.showing': 'عرض',
    'pagination.of': 'من',

    // Filters
    'filter.quick': 'سريع:',
    'filter.custom': 'مخصص:',
    'filter.all': 'الكل',
    'filter.today': 'اليوم',
    'filter.yesterday': 'أمس',
    'filter.thisWeek': 'هذا الأسبوع',
    'filter.lastWeek': 'الأسبوع الماضي',
    'filter.thisMonth': 'هذا الشهر',
    'filter.lastMonth': 'الشهر الماضي',
    'filter.apply': 'تطبيق',
    'filter.clear': 'مسح',
    'filter.selectCategory': 'اختر الفئة',
    'filter.selectServiceType': 'اختر نوع الخدمة',
    'filter.inquiry': 'الاستفسار',

    // Metrics
    'metrics.totalCalls': 'إجمالي المكالمات',
    'metrics.resolved': 'تم حلها',
    'metrics.pending': 'قيد الانتظار',
    'metrics.avgDuration': 'متوسط المدة',
    'metrics.resolutionRate': 'معدل الحل',
    'metrics.highUrgencyRate': 'معدل الطوارئ العالية',
    'metrics.atRiskCalls': 'مكالمات معرضة للخطر',
    'metrics.peakHour': 'ساعة الذروة',
    'metrics.resolutionRateCard': 'معدل الحل',
    'metrics.avgCallLength': 'متوسط طول المكالمة',
    'metrics.highExtraHighUrgency': 'طوارئ عالية + عالية جداً',
    'metrics.highUrgencyPending': 'طوارئ عالية قيد الانتظار',
    'metrics.calls': 'مكالمات',
    'metrics.ofAllCalls': 'من جميع المكالمات',

    // Charts
    'charts.callVolume': 'حجم المكالمات عبر الوقت',
    'charts.callVolumeDesc': 'اتجاهات حجم المكالمات اليومية',
    'charts.statusDist': 'توزيع الحالة',
    'charts.statusDistDesc': 'المكالمات المحللة مقابل المكالمات قيد الانتظار',
    'charts.callsByDay': 'المكالمات حسب اليوم',
    'charts.callsByDayDesc': 'توزيع المكالمات حسب يوم الأسبوع',
    'charts.durationDist': 'توزيع مدة المكالمات',
    'charts.durationDistDesc': 'تفصيل مدة المكالمات',
    'charts.peakHours': 'ساعات الذروة',
    'charts.peakHoursDesc': 'أوقات الذروة خلال اليوم (أعلى 8)',
    'charts.resolutionTrend': 'اتجاه معدل الحل',
    'charts.avgDurationByDay': 'متوسط المدة حسب اليوم',
    'charts.cumulativeCalls': 'المكالمات التراكمية',
    'charts.statusByDay': 'الحالة حسب اليوم',
    'charts.callsByCategory': 'المكالمات حسب الفئة',
    'charts.callsByCategoryDesc': 'أهم الفئات حسب حجم المكالمات',
    'charts.callsByService': 'المكالمات حسب نوع الخدمة',
    'charts.callsByServiceDesc': 'توزيع نوع الخدمة',
    'charts.urgencyDist': 'توزيع الأولوية',
    'charts.urgencyDistDesc': 'المكالمات حسب مستوى الأولوية',
    'charts.urgencyAnalysis': 'تحليل الأولوية',
    'charts.urgencyAnalysisDesc': 'المكالمات المحللة وقيد الانتظار حسب مستوى الأولوية',
    'charts.avgHandleTime': 'متوسط وقت المعالجة حسب الفئة',
    'charts.avgHandleTimeDesc': 'متوسط مدة المكالمة حسب الفئة',
    'charts.serviceEfficiency': 'كفاءة نوع الخدمة',
    'charts.serviceEfficiencyDesc': 'معدل الحل بالنسبة المئوية حسب نوع الخدمة',

    // Days
    'days.sun': 'الأحد',
    'days.mon': 'الاثنين',
    'days.tue': 'الثلاثاء',
    'days.wed': 'الأربعاء',
    'days.thu': 'الخميس',
    'days.fri': 'الجمعة',
    'days.sat': 'السبت',

    // Duration
    'duration.0-30s': '0-30ث',
    'duration.30-60s': '30-60ث',
    'duration.1-2m': '1-2د',
    'duration.2-5m': '2-5د',
    'duration.5m+': '5د+',

    // Modals
    'modal.recordDetails': 'تفاصيل السجل',
    'modal.deleteTitle': 'حذف السجل',
    'modal.deleteMessage': 'هل أنت متأكد من حذف هذا السجل؟ لا يمكن التراجع عن هذا الإجراء.',
    'modal.bulkDeleteTitle': 'حذف السجلات',
    'modal.bulkDeleteMessage': 'هل أنت متأكد من حذف {count} سجلات؟ لا يمكن التراجع عن هذا الإجراء.',

    // Actions
    'actions.markResolved': 'وضع علامة تم الحل',
    'actions.markPending': 'وضع علامة قيد الانتظار',
    'actions.playAudio': 'تشغيل الصوت',
    'actions.stopAudio': 'إيقاف الصوت',
    'actions.exportCSV': 'تصدير CSV',
    'actions.exportPDF': 'تصدير PDF',

    // Toast
    'toast.recordUpdated': 'تم تحديث السجل بنجاح',
    'toast.recordDeleted': 'تم حذف السجل بنجاح',
    'toast.recordsDeleted': 'تم حذف {count} سجلات بنجاح',
    'toast.exportSuccess': 'اكتمل التصدير بنجاح',
    'toast.errorOccurred': 'حدث خطأ',
    'toast.connectionLost': 'فقد الاتصال. جاري إعادة الاتصال...',
    'toast.connectionRestored': 'تم استعادة الاتصال',

    // Empty states
    'empty.noRecords': 'لا توجد سجلات',
    'empty.noRecordsDesc': 'الجدول فارغ أو لا توجد بيانات مطابقة للبحث.',
    'empty.noCategory': 'لا توجد بيانات الفئات',
    'empty.noServiceType': 'لا توجد بيانات أنواع الخدمات',

    // Accessibility
    'a11y.toggleTheme': 'تبديل المظهر',
    'a11y.toggleLanguage': 'تبديل اللغة',
    'a11y.toggleSidebar': 'تبديل الشريط الجانبي',
    'a11y.sortColumn': 'ترتيب حسب {column}',
    'a11y.filterRecords': 'تصفية السجلات',
    'a11y.exportData': 'تصدير البيانات',

    // Placeholders
    'placeholder.search': 'البحث في السجلات...',
    'placeholder.phoneNumber': 'رقم الهاتف',
    'placeholder.name': 'الاسم',
    'placeholder.notes': 'ملاحظات...',

    // Appointments
    'appointments.addNew': 'إضافة موعد',
    'appointments.edit': 'تعديل الموعد',
    'appointments.searchPlaceholder': 'البحث في المواعيد...',
    'appointments.patientName': 'اسم المريض',
    'appointments.patientPlaceholder': 'أدخل اسم المريض',
    'appointments.date': 'التاريخ',
    'appointments.time': 'الوقت',
    'appointments.provider': 'المزود',
    'appointments.providerPlaceholder': 'أدخل اسم المزود',
    'appointments.status': 'الحالة',
    'appointments.notes': 'ملاحظات',
    'appointments.notesPlaceholder': 'أدخل ملاحظات',
    'appointments.allStatus': 'جميع الحالات',
    'appointments.scheduled': 'مجدول',
    'appointments.confirmed': 'مؤكد',
    'appointments.completed': 'مكتمل',
    'appointments.cancelled': 'ملغى',
    'appointments.noShow': 'لم يحضر',
    'appointments.noAppointments': 'لا توجد مواعيد',
    'appointments.confirmDelete': 'هل أنت متأكد من حذف هذا الموعد؟',

    // Inventory
    'inventory.addNew': 'إضافة صنف',
    'inventory.edit': 'تعديل الصنف',
    'inventory.searchPlaceholder': 'البحث في المخزون...',
    'inventory.itemName': 'اسم الصنف',
    'inventory.itemPlaceholder': 'أدخل اسم الصنف',
    'inventory.sku': 'رمز المنتج',
    'inventory.quantity': 'الكمية',
    'inventory.unit': 'الوحدة',
    'inventory.price': 'السعر',
    'inventory.category': 'الفئة',
    'inventory.categoryPlaceholder': 'أدخل الفئة',
    'inventory.expiryDate': 'تاريخ الانتهاء',
    'inventory.status': 'الحالة',
    'inventory.allCategories': 'جميع الفئات',
    'inventory.totalItems': 'إجمالي الأصناف',
    'inventory.totalValue': 'القيمة الإجمالية',
    'inventory.lowStock': 'مخزون منخفض',
    'inventory.inStock': 'متوفر',
    'inventory.outOfStock': 'نفذ المخزون',
    'inventory.noItems': 'لا توجد أصناف في المخزون',
    'inventory.confirmDelete': 'هل أنت متأكد من حذف هذا الصنف؟',

    // User Menu
    'user.profile': 'الملف الشخصي',
    'user.settings': 'الإعدادات',
    'user.logout': 'تسجيل الخروج',
    'user.welcome': 'مرحباً',

    // Field Labels
    'field.id': 'المعرف',
    'field.date': 'التاريخ',
    'field.name': 'الاسم',
    'field.phone': 'الهاتف',
    'field.category': 'الفئة',
    'field.service': 'الخدمة',
    'field.inquiry': 'الاستفسار',
    'field.duration': 'المدة',
    'field.status': 'الحالة',
    'field.urgency': 'الأولوية',
    'field.notes': 'ملاحظات',
    'field.summary': 'ملخص',
    'field.conversationId': 'معرف المحادثة',
    'field.audio': 'الصوت',

    // Urgency Levels
    'urgency.low': 'منخفضة',
    'urgency.medium': 'متوسطة',
    'urgency.high': 'عالية',
    'urgency.extraHigh': 'عالية جداً',
    'urgency.no': 'لا',

    // Quick Filters
    'quickFilter.quick': 'سريع:',

    // Date Range
    'dateRange.from': 'من',
    'dateRange.until': 'حتى',
    'dateRange.last7Days': 'آخر 7 أيام',
    'dateRange.last30Days': 'آخر 30 يوم',
    'dateRange.last90Days': 'آخر 90 يوم',
  },
};

const initialState: LanguageState = {
  language: 'en',
  setLanguage: () => null,
  t: () => '',
};

export const LanguageContext = createContext<LanguageState>(initialState);

export function LanguageProvider({
  children,
  defaultLanguage = 'en',
  storageKey = 'vite-ui-language',
}: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem(storageKey) as Language) || defaultLanguage;
    }
    return defaultLanguage;
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(storageKey, lang);
  }, [storageKey]);

  const t = useCallback((key: string): string => {
    return translations[language][key] || key;
  }, [language]);

  // Update document direction and lang attribute
  useEffect(() => {
    const html = document.documentElement;
    html.dir = language === 'ar' ? 'rtl' : 'ltr';
    html.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Helper hook for date formatting based on language
export function useFormattedDate() {
  const { language, t } = useLanguage();

  const format = useCallback((date: Date | string, formatStr: string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const options: Intl.DateTimeFormatOptions = {};

    // Map format strings to Intl options
    if (formatStr === 'short') {
      options.dateStyle = 'short';
    } else if (formatStr === 'medium') {
      options.dateStyle = 'medium';
    } else if (formatStr === 'long') {
      options.dateStyle = 'long';
    } else if (formatStr === 'full') {
      options.dateStyle = 'full';
    } else if (formatStr === 'time') {
      options.timeStyle = 'short';
    } else if (formatStr === 'datetime') {
      options.dateStyle = 'medium';
      options.timeStyle = 'short';
    }

    return new Intl.DateTimeFormat(language === 'ar' ? 'ar-SA' : 'en-US', options).format(dateObj);
  }, [language]);

  return { format, language, t };
}
